import { useMemo } from 'react';

import { useStarterDashboardV2PageData } from '#src/app/pages/starterDashboardV2/pageContext';
import { useNextStepsExperiment } from '#src/app/pages/starterDashboardV2/shared/hooks';

import { TASKS_CONFIGS } from '../../components/NextSteps/constants';

type UseNextStepsStatsReturn = Readonly<{
	stats: Readonly<{
		completed: number;
		count: number;
	}>;
	allTasksCompleted: boolean;
	shouldShowNextStepsTop: boolean;
	shouldShowNextStepsBottom: boolean;
}>;

export function useNextStepsStats(): UseNextStepsStatsReturn {
	const { data } = useStarterDashboardV2PageData();
	const {
		data: { isEnabled: isNextStepsEnabled },
	} = useNextStepsExperiment();

	// combine active + completed (within last 24hrs) tasks
	const stats = useMemo(() => {
		if (
			data.petition.activeTasks.__typename !== 'PetitionTasksConnection' ||
			data.petition.completedTasks.__typename !== 'PetitionTasksConnection'
		) {
			return { count: 0, completed: 0 };
		}

		const allTasks = [...data.petition.activeTasks.nodes, ...data.petition.completedTasks.nodes];

		// Filter tasks that are in TASKS_CONFIGS
		const relevantTasks = allTasks.filter((task) => Object.hasOwn(TASKS_CONFIGS, task.name));

		const completed = relevantTasks.filter((task) => task.status === 'COMPLETED').length;
		const count = relevantTasks.length;

		return {
			count,
			completed,
		};
	}, [data.petition.activeTasks, data.petition.completedTasks]);

	const allTasksCompleted = stats.count > 0 && stats.completed === stats.count;
	const shouldShowNextStepsTop = isNextStepsEnabled && !allTasksCompleted;
	const shouldShowNextStepsBottom = allTasksCompleted && isNextStepsEnabled;

	return {
		stats,
		allTasksCompleted,
		shouldShowNextStepsTop,
		shouldShowNextStepsBottom,
	};
}
